import React, { Component } from "react";
import { Link, Redirect } from "react-router-dom";
import client from "@culturehq/client";
import { Button, Panel } from "@culturehq/components";

import { errorService, t, Trans } from "app/utils";

import { TimeDiff } from "components/common/Time";
import ChqModalConfirm from "components/common/ChqModalConfirm";
import SurveyItemList from "./SurveyItemList";
import SurveyStatus from "./SurveyStatus";

const SurveyDraftActions = ({ survey, onSurveyActivated }) => {
  const disabled = survey.surveyItems.length < 1;

  return (
    <>
      <ChqModalConfirm
        onAccept={() => onSurveyActivated(survey)}
        trigger={onTrigger => (
          <Button onClick={onTrigger} icon="flash" disabled={disabled}>
            <Trans>Activate</Trans>
          </Button>
        )}
      >
        <Trans>
          Are you sure you would like to activate this survey? Activating this
          survey will allow users to respond to it from the survey page and the
          widget on the home page.
        </Trans>
      </ChqModalConfirm>{" "}
      <Link className="action link" to="/surveys/manage">
        <Trans>Keep as Draft</Trans>
      </Link>
    </>
  );
};

const SurveyInactiveActions = ({ survey, onSurveyReactivated }) => {
  const disabled = survey.surveyItems.length < 1;
  return (
    <>
      <ChqModalConfirm
        onAccept={() => onSurveyReactivated(survey)}
        trigger={onTrigger => (
          <Button onClick={onTrigger} icon="flash" disabled={disabled}>
            <Trans>Reactivate</Trans>
          </Button>
        )}
      >
        <Trans>
          Are you sure you would like to reactivate this survey? Reactivating
          this survey will allow users who have not previously responded to it
          respond from the survey page and the widget on the home page.
        </Trans>
      </ChqModalConfirm>{" "}
      <Link className="action link" to="/surveys/manage">
        <Trans>Keep as Inactive</Trans>
      </Link>
    </>
  );
};

const SurveyActiveActions = () => (
  <Link className="action link" to="/surveys/manage">
    ← <Trans>Go back to survey list</Trans>
  </Link>
);

const SurveyEditTitleStatus = ({ survey: { activeModifiedAt }, status }) => {
  if (status.isDraft()) {
    return <i>{status.toString()}</i>;
  }

  return (
    <>
      {status.toString()} since{" "}
      <TimeDiff time={activeModifiedAt} format="%b %e, %Y, %l:%M %p" />
    </>
  );
};

const SurveyEditTitle = ({ survey, status }) => (
  <>
    <span className="pull-right">
      <SurveyEditTitleStatus survey={survey} status={status} />
    </span>
    {survey.title}
  </>
);

class SurveyEdit extends Component {
  state = { survey: null, loading: true };

  async componentDidMount() {
    try {
      const {
        match: {
          params: { surveyId }
        }
      } = this.props;
      const { survey } = await client.getSurvey({ surveyId });

      this.setState({ survey, loading: false });
    } catch (error) {
      errorService.error(error);
      this.setState({ survey: {}, loading: false });
    }
  }

  handleSurveyActivated = async () => {
    this.setState({ loading: true });

    try {
      const {
        survey: { id: surveyId }
      } = this.state;
      const { survey } = await client.activateSurvey({ surveyId });

      this.setState({ survey, loading: false });
    } catch (error) {
      errorService.error(error);
      this.setState({ survey: {}, loading: false });
    }
  };

  handleSurveyEdited = survey => {
    this.setState({ survey });
  };

  handleSurveyDeleted = () => {
    this.setState({ redirectTo: "/surveys" });
  };

  render() {
    const { survey, loading, redirectTo } = this.state;

    if (redirectTo) {
      return <Redirect to={redirectTo} />;
    }

    const status = SurveyStatus.from(survey);
    const title = survey ? (
      <SurveyEditTitle survey={survey} status={status} />
    ) : (
      t("Loading...")
    );

    return (
      <Panel limitWidth>
        <Panel.Heading primary>
          {title}
        </Panel.Heading>
        <Panel.LoaderBody loading={loading}>
          <SurveyItemList
            survey={survey}
            event={null}
            onSurveyEdited={this.handleSurveyEdited}
            onSurveyDeleted={this.handleSurveyDeleted}
          />
          {status.isDraft() && (
            <SurveyDraftActions
              survey={survey}
              onSurveyActivated={this.handleSurveyActivated}
            />
          )}
          {status.isInactive() && (
            <SurveyInactiveActions
              survey={survey}
              onSurveyReactivated={this.handleSurveyActivated}
            />
          )}
          {status.isActive() && <SurveyActiveActions />}
        </Panel.LoaderBody>
      </Panel>
    );
  }
}

export default SurveyEdit;
